<template>
    <div class="myFiles">
        <!-- Header Principal com Filtros e Estatísticas -->
        <Box
            title="Meus Arquivos"
            sub-title="Gerencie todos os seus documentos em um só lugar"
            icon="folder-open"
            :variant="BOX_VARIANTS.DEFAULT"
            :actions="headerActions"
            @action-click="handleHeaderAction"
        >
            <!-- Seção de Filtros -->
            <FilterSection
                label="Filtrar por:"
                search-placeholder="Buscar arquivos..."
                :search-value="searchQuery"
                :filters="filterOptions"
                :filter-values="filters"
                @update:search="updateSearch"
                @update:filter="updateFilter"
                @clear-filters="clearFilters"
            />

            <!-- Estatísticas dos Arquivos -->
            <div class="stats">
                <div class="stats_item">
                    <span class="stats_number">{{ allFiles.length }}</span>
                    <span class="stats_label">arquivos</span>
                </div>
                <div class="stats_item stats_item--warning">
                    <span class="stats_number">{{ expiringCount }}</span>
                    <span class="stats_label">vencendo</span>
                </div>
                <div class="stats_item stats_item--danger">
                    <span class="stats_number">{{ expiredCount }}</span>
                    <span class="stats_label">vencidos</span>
                </div>

            </div>
        </Box>


        <!-- Lista de arquivos -->
        <div class="myFiles_content">
            <div v-if="loading" class="myFiles_loading">
                <app-spinner text="Carregando arquivos..." />
            </div>

            <div v-else-if="filteredFiles.length === 0" class="myFiles_empty">
                <EmptyState
                    :icon="hasActiveFilters ? 'search' : 'folder-open'"
                    :title="hasActiveFilters ? 'Nenhum arquivo encontrado' : 'Nenhum arquivo adicionado ainda'"
                    :description="hasActiveFilters
                        ? 'Tente ajustar os filtros para encontrar o que procura'
                        : 'Comece adicionando seus primeiros arquivos'"
                >
                    <template #actions>
                        <sw-button
                            :name="hasActiveFilters ? 'Adicionar Arquivo' : 'Adicionar Primeiro Arquivo'"
                            :icon="hasActiveFilters ? 'upload' : 'plus'"
                            color="blue"
                            @click="openUploadModal"
                        />
                    </template>
                </EmptyState>
            </div>

            <div v-else class="myFiles_grid">
                <FileCard
                    v-for="file in filteredFiles"
                    :key="file.id"
                    :document="file"
                    :loading="loading"
                />
            </div>
        </div>

        <!-- Paginação (se necessário no futuro) -->
        <div v-if="filteredFiles.length > 0" class="myFiles_pagination">
            <span class="myFiles_paginationInfo">
                Mostrando {{ filteredFiles.length }} de {{ allFiles.length }} arquivos
            </span>
        </div>

        <!-- Modais -->
        <!-- Modal Gerenciar Categorias -->
        <sw-modal
            :visible="showManageCategoriesModal"
            @update="closeManageCategoriesModal"
        >
            <template #header>
                <ModalHeader
                    icon="cog"
                    title="Gerenciar Categorias"
                    subtitle="Visualize, edite e organize suas categorias"
                    :color="MODAL_HEADER_COLORS.BLUE"
                />
            </template>

            <template #content>
                <div class="manage-categories">
                    <TabsComponent
                        :tabs="categoryTabs"
                        defaultTab="list"
                        @tab-change="onTabChange"
                    >
                        <!-- Aba Lista de Categorias -->
                        <template #list>
                            <div class="categories-list">
                                <div class="row">
                                    <div class="grid_col_4 grid_col_m_6 grid_col_s_12" v-for="category in existingCategories" :key="category.id">
                                        <div class="category-item">
                                            <!-- Cabeçalho com ícone -->
                                            <div class="category-header">
                                                <div class="category-icon">
                                                    <sw-icon name="folder" />
                                                </div>
                                            </div>

                                            <!-- Conteúdo da categoria -->
                                            <div class="category-content">
                                                <!-- Informações da categoria -->
                                                <div v-if="editingCategory?.id !== category.id" class="category-info">
                                                    <span class="category-name">{{ category.name }}</span>
                                                    <span class="category-count">{{ category.count }} arquivos</span>

                                                    <!-- Ações da categoria -->
                                                    <div class="category-actions">
                                                        <sw-button
                                                            icon="pencil"
                                                            hover-color="blue"
                                                            circleIcon
                                                            size="small"
                                                            @click="startEditCategory(category)"
                                                        />
                                                        <sw-button
                                                            icon="cross"
                                                            hover-color="red"
                                                            circleIcon
                                                            size="small"
                                                            @click="openConfirmDeleteModal(category)"
                                                        />
                                                    </div>
                                                </div>

                                                <!-- Modo de edição -->
                                                <div v-if="editingCategory?.id === category.id" class="category-edit">
                                                    <sw-input
                                                        v-model="newCategoryName"
                                                        placeholder="Nome da categoria"
                                                        :validation="categoryNameValidation"
                                                    />
                                                    <div class="edit-actions">
                                                        <sw-button
                                                            circleIcon
                                                            icon="checkmark"
                                                            color="green"
                                                            size="small"
                                                            :disabled="!isCategoryNameValid"
                                                            @click="saveCategory"
                                                        />
                                                        <sw-button
                                                            name="Cancelar"
                                                            size="small"
                                                            @click="cancelEdit"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="existingCategories.length === 0" class="empty-state">
                                    <sw-icon name="folder-plus" />
                                    <p>Nenhuma categoria encontrada</p>
                                    <small>Use a aba "Nova Categoria" para criar sua primeira categoria</small>
                                </div>
                            </div>
                        </template>

                        <!-- Aba Adicionar Nova Categoria -->
                        <template #add>
                            <div class="add-category">
                                <div class="add-category-form">
                                    <sw-input
                                        v-model="newCategoryName"
                                        label="Nome da categoria *"
                                        placeholder="Ex: Contratos 2024"
                                        :validation="categoryNameValidation"
                                    />

                                    <AlertMessage
                                        :variant="ALERT_VARIANTS.INFO"
                                        title="Dicas para nomear sua categoria"
                                        description="Escolha nomes claros e organizados para facilitar a localização dos seus documentos. Exemplos: 'Documentos Pessoais 2024', 'Contratos - Empresa XYZ', 'Família Silva - Saúde', 'Imóveis e Financeiro'."
                                        icon="info"
                                    />

                                    <div class="form-actions">
                                        <sw-button
                                            name="Criar Categoria"
                                            color="blue"
                                            :disabled="!isCategoryNameValid"
                                            @click="saveCategory"
                                        />
                                    </div>
                                </div>
                            </div>
                        </template>
                    </TabsComponent>
                </div>
            </template>
        </sw-modal>

        <!-- Modal Gerenciar Empresas -->
        <sw-modal
            :visible="showManageClientsModal"
            @update="closeManageClientsModal"
        >
            <template #header>
                <ModalHeader
                    icon="office"
                    title="Gerenciar Empresas"
                    subtitle="Visualize, edite e organize suas empresas"
                    :color="MODAL_HEADER_COLORS.BLUE"
                />
            </template>

            <template #content>
                <div class="manage-clients">
                    <TabsComponent
                        :tabs="clientTabs"
                        defaultTab="list"
                        @tab-change="onClientTabChange"
                    >
                        <!-- Aba Lista de Empresas -->
                        <template #list>
                            <div class="clients-list">
                                <div class="row">
                                    <div class="grid_col_4 grid_col_m_6 grid_col_s_12" v-for="client in existingClients" :key="client.id">
                                        <div class="client-item">
                                            <!-- Cabeçalho com ícone/imagem -->
                                            <div class="client-header">
                                                <div class="client-icon">
                                                    <img
                                                        v-if="client.image"
                                                        :src="client.image"
                                                        :alt="`Logo de ${client.name}`"
                                                        class="client-logo"
                                                    />
                                                    <sw-icon v-else name="office" />
                                                </div>
                                            </div>

                                            <!-- Conteúdo da empresa -->
                                            <div class="client-content">
                                                <!-- Informações da empresa -->
                                                <div v-if="editingClient?.id !== client.id" class="client-info">
                                                    <span class="client-name">{{ client.name }}</span>
                                                    <span class="client-count">{{ client.count }} arquivos</span>

                                                    <!-- Ações da empresa -->
                                                    <div class="client-actions">
                                                        <sw-button
                                                            icon="pencil"
                                                            hover-color="blue"
                                                            circleIcon
                                                            size="small"
                                                            @click="startEditClient(client)"
                                                        />
                                                        <sw-button
                                                            icon="cross"
                                                            hover-color="red"
                                                            circleIcon
                                                            size="small"
                                                            @click="openConfirmDeleteClientModal(client)"
                                                        />
                                                    </div>
                                                </div>

                                                <!-- Modo de edição -->
                                                <div v-if="editingClient?.id === client.id" class="client-edit">
                                                    <sw-input
                                                        v-model="newClientName"
                                                        placeholder="Nome da empresa"
                                                        :validation="clientNameValidation"
                                                    />
                                                    <div class="edit-actions">
                                                        <sw-button
                                                            name="Salvar"
                                                            color="blue"
                                                            size="small"
                                                            :disabled="!isClientNameValid"
                                                            @click="saveClient"
                                                        />
                                                        <sw-button
                                                            name="Cancelar"
                                                            color="gray"
                                                            size="small"
                                                            @click="cancelEditClient"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="existingClients.length === 0" class="empty-state">
                                    <sw-icon name="office" />
                                    <p>Nenhuma empresa encontrada</p>
                                    <small>Use a aba "Nova Empresa" para criar sua primeira empresa</small>
                                </div>
                            </div>
                        </template>

                        <!-- Aba Adicionar Nova Empresa -->
                        <template #add>
                            <div class="add-client">
                                <div class="add-client-form">
                                    <sw-input
                                        v-model="newClientName"
                                        label="Nome da empresa *"
                                        placeholder="Ex: Tech Solutions Ltda."
                                        :validation="clientNameValidation"
                                    />

                                    <sw-input-file
                                        v-model="newClientLogo"
                                        label="Logo da empresa (opcional)"
                                        placeholder="Selecione o logo da empresa"
                                        accept="image/*"
                                        :preview="true"
                                    />

                                    <AlertMessage
                                        :variant="ALERT_VARIANTS.INFO"
                                        title="Dicas para nomear sua empresa"
                                        description="Use nomes completos e oficiais das empresas para facilitar a identificação. Exemplos: 'Tech Solutions Ltda.', 'Indústria Costa S.A.', 'Comercial Silva & Associados', 'Restaurante Sabor Caseiro'."
                                        icon="info"
                                    />

                                    <div class="form-actions">
                                        <sw-button
                                            name="Criar Empresa"
                                            color="blue"
                                            :disabled="!isClientNameValid"
                                            @click="saveClient"
                                        />
                                    </div>
                                </div>
                            </div>
                        </template>
                    </TabsComponent>
                </div>
            </template>
        </sw-modal>

        <!-- Modal Confirmar Exclusão de Categoria -->
        <sw-modal
            :visible="showConfirmDeleteCategory"
            @update="closeConfirmDeleteModal"
        >
            <template #header>
                <ModalHeader
                    icon="cross"
                    title="Excluir Categoria"
                    subtitle="Confirme a exclusão desta categoria"
                    :color="MODAL_HEADER_COLORS.RED"
                />
            </template>

            <template #content>
                <div class="confirmDeleteModal_content">
                    <div v-if="categoryToDelete" class="confirmDelete_info">
                        <AlertMessage
                            :variant="filesInCategoryToDelete.length > 0 ? ALERT_VARIANTS.WARNING : ALERT_VARIANTS.INFO"
                            :title="filesInCategoryToDelete.length > 0 ? 'Atenção! Esta ação não pode ser desfeita' : 'Categoria vazia - Exclusão segura'"
                            :description="getDeleteAlertDescription()"
                        />

                        <div class="confirmDelete_categoryDetails">
                            <div class="categoryCard">
                                <div class="categoryCard_header">
                                    <div class="categoryCard_icon">
                                        <sw-icon name="folder" />
                                    </div>
                                    <div class="categoryCard_info">
                                        <h4>{{ categoryToDelete.name }}</h4>
                                        <span class="file-count">{{ categoryToDelete.count }} {{ categoryToDelete.count === 1 ? 'arquivo' : 'arquivos' }}</span>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filesInCategoryToDelete.length > 0" class="confirmDelete_filesList">
                                <h5>📁 Arquivos que serão afetados:</h5>
                                <div class="filesList">
                                    <div v-for="file in filesInCategoryToDelete.slice(0, 5)" :key="file.id" class="fileItem">
                                        <sw-icon name="file-text" />
                                        <span>{{ file.name }}</span>
                                        <span class="file-type">{{ file.type }}</span>
                                    </div>
                                    <div v-if="filesInCategoryToDelete.length > 5" class="moreFiles">
                                        📄 +{{ filesInCategoryToDelete.length - 5 }} outros arquivos
                                    </div>
                                </div>

                                <div class="impact-info">
                                    <div class="impact-item">
                                        <sw-icon name="checkmark" />
                                        <span>Os arquivos <strong>permanecerão salvos</strong> no sistema</span>
                                    </div>
                                    <div class="impact-item">
                                        <sw-icon name="point-up" />
                                        <span>Apenas perderão a <strong>organização por categoria</strong></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="confirmDeleteActions">
                        <sw-button
                            name="Confirmar Exclusão"
                            color="red"
                            @click="confirmDeleteCategory"
                        />
                        <sw-button
                            name="Cancelar"
                            color="gray"
                            @click="closeConfirmDeleteModal"
                        />
                    </div>
                </div>
            </template>
        </sw-modal>

        <!-- Modal Confirmar Exclusão de Empresa -->
        <sw-modal
            :visible="showConfirmDeleteClient"
            @update="closeConfirmDeleteClientModal"
        >
            <template #header>
                <ModalHeader
                    icon="cross"
                    title="Excluir Empresa"
                    subtitle="Confirme a exclusão desta empresa"
                    :color="MODAL_HEADER_COLORS.RED"
                />
            </template>

            <template #content>
                <div class="confirmDeleteModal_content">
                    <div v-if="clientToDelete" class="confirmDelete_info">
                        <AlertMessage
                            :variant="filesInClientToDelete.length > 0 ? ALERT_VARIANTS.WARNING : ALERT_VARIANTS.INFO"
                            :title="filesInClientToDelete.length > 0 ? 'Atenção! Esta ação não pode ser desfeita' : 'Empresa vazia - Exclusão segura'"
                            :description="getDeleteClientAlertDescription()"
                        />

                        <div class="confirmDelete_categoryDetails">
                            <div class="categoryCard">
                                <div class="categoryCard_header">
                                    <div class="categoryCard_icon" style="background: linear-gradient(135deg, var(--colorOrange, #f97316), var(--colorOrangeDark, #ea580c));">
                                        <img
                                            v-if="clientToDelete.image"
                                            :src="clientToDelete.image"
                                            :alt="`Logo de ${clientToDelete.name}`"
                                            class="categoryCard_logo"
                                        />
                                        <sw-icon v-else name="office" />
                                    </div>
                                    <div class="categoryCard_info">
                                        <h4>{{ clientToDelete.name }}</h4>
                                        <span class="file-count">{{ clientToDelete.count }} {{ clientToDelete.count === 1 ? 'arquivo' : 'arquivos' }}</span>
                                        <span class="client-email">{{ clientToDelete.email }}</span>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filesInClientToDelete.length > 0" class="confirmDelete_filesList">
                                <h5>🏢 Arquivos associados à empresa:</h5>
                                <div class="filesList">
                                    <div v-for="file in filesInClientToDelete.slice(0, 5)" :key="file.id" class="fileItem">
                                        <sw-icon name="file-text" />
                                        <span>{{ file.name }}</span>
                                        <span class="file-type">{{ file.type }}</span>
                                    </div>
                                    <div v-if="filesInClientToDelete.length > 5" class="moreFiles">
                                        📄 +{{ filesInClientToDelete.length - 5 }} outros arquivos
                                    </div>
                                </div>

                                <div class="impact-info">
                                    <div class="impact-item">
                                        <sw-icon name="checkmark" />
                                        <span>Os arquivos <strong>permanecerão salvos</strong> no sistema</span>
                                    </div>
                                    <div class="impact-item">
                                        <sw-icon name="point-up" />
                                        <span>Apenas perderão a <strong>associação com a empresa</strong></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="confirmDeleteActions">
                        <sw-button
                            name="Confirmar Exclusão"
                            color="red"
                            @click="confirmDeleteClient"
                        />
                        <sw-button
                            name="Cancelar"
                            color="gray"
                            @click="closeConfirmDeleteClientModal"
                        />
                    </div>
                </div>
            </template>
        </sw-modal>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue';
import { notify } from 'sysweb-vue-components/src/global';
import FileCard from '@/components/FileCard.vue';
import AlertMessage from '@/components/alerts/AlertMessage.vue';
import Box from '@Component/boxs/BoxComponent';
import FilterSection from '@Component/filters/FilterSection.vue';
import TabsComponent from '@Component/tabs/TabsComponent.vue';
import ModalHeader from '@Component/modals/ModalHeader.vue';
import EmptyState from '@Component/empty-states/EmptyState.vue';
import {
    mockFiles,
    mockFileGroups,
    mockClients,
    mockFileCategories
} from '@/data/mockData';
import {
    PERSON_TYPES,
    DOCUMENT_OWNERSHIP,
    ALERT_VARIANTS,
    BOX_VARIANTS,
    MODAL_HEADER_COLORS
} from '@/constants';

// ===========================
// INJEÇÕES DE DEPENDÊNCIA
// ===========================

// Injetar a função de abrir modal de upload do Dashboard
const openUploadModal = inject('openUploadModal');

// ===========================
// ESTADO DA APLICAÇÃO
// ===========================

// Estado de carregamento e dados
const loading = ref(false);
const allFiles = ref([]);
const searchQuery = ref('');


// Estado dos filtros - todos iniciam vazios para mostrar todos os arquivos
const filters = ref({
    personType: '',
    type: '',
    category: '',
    client: ''
});

// Estado dos modais
const modals = {
    showManageClients: ref(false),
    showManageCategories: ref(false),
    showConfirmDeleteCategory: ref(false),
    showConfirmDeleteClient: ref(false),
    newCategoryName: ref(''),
    newClientName: ref(''),
    newClientLogo: ref(null),
    editingCategory: ref(null),
    editingClient: ref(null),
    categoryToDelete: ref(null),
    clientToDelete: ref(null)
};

// Destructuring para compatibilidade
const {
    showManageClients: showManageClientsModal,
    showManageCategories: showManageCategoriesModal,
    showConfirmDeleteCategory,
    showConfirmDeleteClient,
    newCategoryName,
    newClientName,
    newClientLogo,
    editingCategory,
    editingClient,
    categoryToDelete,
    clientToDelete
} = modals;

// ===========================
// OPÇÕES DE FILTROS
// ===========================

// Opções dinâmicas baseadas nos arquivos existentes com suboptions (igual ao UploadFileForm)
const typeFilterOptions = computed(() => {
    // Função para obter opções de documentos com suboptions
    const getDocumentTypeOptions = () => {
        let baseOptions = [];

        // Se não há tipo de pessoa selecionado, mostrar ambos
        if (!filters.value.personType) {
            baseOptions = [...mockFileCategories.NATURAL, ...mockFileCategories.COMPANY];
        } else if (filters.value.personType === PERSON_TYPES.FISICA) {
            baseOptions = mockFileCategories.NATURAL;
        } else if (filters.value.personType === PERSON_TYPES.JURIDICA) {
            baseOptions = mockFileCategories.COMPANY;
        }

        // Verificar quais tipos de documentos existem nos arquivos atuais
        const existingTypes = new Set(allFiles.value.map(file => file.type));

        // Processar opções com suboptions (igual ao UploadFileForm)
        const processedOptions = baseOptions.map(option => {
            return {
                ...option,
                suboptions: option.suboptions || []
            };
        });

        return [
            { value: '', label: 'Todos os tipos' },
            ...processedOptions
        ];
    };

    return getDocumentTypeOptions();
});

// Opções estáticas baseadas nos dados mock
const categoryFilterOptions = computed(() => [
    { value: '', label: 'Todas as categorias' },
    ...mockFileGroups.map(group => ({ value: group.value, label: group.label }))
]);

const clientFilterOptions = computed(() => [
    { value: '', label: 'Todas as empresas' },
    ...mockClients.map(client => ({ value: client.id, label: client.name }))
]);

const personTypeFilterOptions = computed(() => [
    { value: '', label: 'Todos os tipos' },
    { value: PERSON_TYPES.FISICA, label: 'Pessoa Física' },
    { value: PERSON_TYPES.JURIDICA, label: 'Pessoa Jurídica' }
]);

// Configuração dos filtros para o FilterSection
const filterOptions = computed(() => {
    const baseFilters = [
        {
            key: 'personType',
            label: 'Tipo de pessoa',
            options: personTypeFilterOptions.value,
            gridClass: 'grid_col_6'
        }
    ];

    // Só mostrar filtro de tipo de documento após selecionar tipo de pessoa
    if (filters.value.personType) {
        baseFilters.push({
            key: 'type',
            label: 'Tipo de documento',
            options: typeFilterOptions.value,
            gridClass: 'grid_col_6'
        });

        // Adicionar filtro de categoria
        baseFilters.push({
            key: 'category',
            label: 'Categoria',
            options: categoryFilterOptions.value,
            gridClass: 'grid_col_6'
        });
    }

    // Adicionar filtro de empresa apenas se pessoa jurídica estiver selecionada
    if (filters.value.personType === PERSON_TYPES.JURIDICA) {
        baseFilters.push({
            key: 'client',
            label: 'Empresa',
            options: clientFilterOptions.value,
            gridClass: 'grid_col_6'
        });
    }

    return baseFilters;
});

// ===========================
// DADOS COMPUTADOS
// ===========================

// Lista de arquivos filtrada baseada nos critérios ativos
const filteredFiles = computed(() => {
    let result = [...allFiles.value];

    // Aplicar filtro de busca textual
    if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter(file =>
            file.name.toLowerCase().includes(query) ||
            file.type.toLowerCase().includes(query)
        );
    }

    // Aplicar filtros de seleção
    const filterCriteria = [
        { key: 'personType', property: 'personType' },
        { key: 'type', property: 'type' },
        { key: 'category', property: 'group' },
        { key: 'client', property: 'client' }
    ];

    filterCriteria.forEach(({ key, property }) => {
        if (filters.value[key]) {
            result = result.filter(file => file[property] === filters.value[key]);
        }
    });

    return result;
});

// Estatísticas calculadas dos TODOS OS arquivos (não filtrados)
const stats = computed(() => ({
    total: allFiles.value.length,
    expiring: allFiles.value.filter(file => file.status === 'expiring').length,
    expired: allFiles.value.filter(file => file.status === 'expired').length
}));

// Conveniência para compatibilidade
const expiringCount = computed(() => stats.value.expiring);
const expiredCount = computed(() => stats.value.expired);

// Verificação se existem filtros ativos
const hasActiveFilters = computed(() =>
    searchQuery.value.trim() ||
    Object.values(filters.value).some(filter => filter)
);

// Arquivos que pertencem à categoria que será deletada
const filesInCategoryToDelete = computed(() => {
    if (!categoryToDelete.value) return [];

    // Usar o groupValue diretamente da categoria
    const categoryValue = categoryToDelete.value.groupValue;

    if (!categoryValue) return [];

    return allFiles.value.filter(file => file.group === categoryValue);
});

// Arquivos que pertencem ao cliente que será deletado
const filesInClientToDelete = computed(() => {
    if (!clientToDelete.value) return [];

    return allFiles.value.filter(file => file.company?.id === clientToDelete.value.id);
});

// Mock data para categorias existentes (mapeadas com os grupos dos arquivos)
const existingCategories = ref([
    { id: 1, name: 'Trabalho', count: 12, groupValue: 'trabalho' },
    { id: 2, name: 'Pessoal', count: 8, groupValue: 'pessoal' },
    { id: 3, name: 'Família', count: 15, groupValue: 'familia' },
    { id: 4, name: 'Financeiro', count: 22, groupValue: 'financeiro' }
]);

// Mock data para empresas existentes (mapeadas com os IDs dos arquivos)
const existingClients = ref([
    {
        id: 2,
        name: 'Restaurante Sabor Caseiro Ltda',
        email: 'contato@saborcaseiro.com.br',
        count: 0, // será calculado dinamicamente
        image: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=100&h=100&fit=crop&crop=center' // Exemplo de imagem
    },
    {
        id: 3,
        name: 'TechCorp Soluções Digitais',
        email: 'admin@techcorp.com.br',
        count: 0, // será calculado dinamicamente
        image: null // Sem imagem - usará default
    },
    {
        id: 5,
        name: 'Consultoria Empresarial JR',
        email: 'contato@consultoriajr.com.br',
        count: 0, // será calculado dinamicamente
        image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop&crop=center' // Exemplo de imagem
    },
    {
        id: 6,
        name: 'Empresa Fictícia Teste',
        email: 'teste@empresa.com.br',
        count: 0, // será calculado dinamicamente
        image: null // Sem imagem - usará default
    }
]);

// Atualizar contadores de arquivos por empresa
const updateClientCounts = () => {
    existingClients.value.forEach(client => {
        client.count = allFiles.value.filter(file => file.company?.id === client.id).length;
    });
};

// Configuração das abas do modal de categorias
const categoryTabs = computed(() => [
    {
        id: 'list',
        label: 'Categorias',
        icon: 'folder',
        badge: existingCategories.value.length
    },
    {
        id: 'add',
        label: 'Nova Categoria',
        icon: 'box-add'
    }
]);

// Configuração das abas do modal de empresas
const clientTabs = computed(() => [
    {
        id: 'list',
        label: 'Empresas',
        icon: 'office',
        badge: existingClients.value.length
    },
    {
        id: 'add',
        label: 'Nova Empresa',
        icon: 'box-add'
    }
]);

// Validações dos modais
const categoryNameValidation = computed(() => ({
    isValid: newCategoryName.value.trim().length >= 2,
    error: {
        message: newCategoryName.value.trim().length < 2 && newCategoryName.value.length > 0
            ? 'Nome deve ter pelo menos 2 caracteres'
            : ''
    }
}));

const isCategoryNameValid = computed(() =>
    newCategoryName.value.trim().length >= 2
);

const clientNameValidation = computed(() => ({
    isValid: newClientName.value.trim().length >= 2,
    error: {
        message: newClientName.value.trim().length < 2 && newClientName.value.length > 0
            ? 'Nome deve ter pelo menos 2 caracteres'
            : ''
    }
}));

const isClientNameValid = computed(() =>
    newClientName.value.trim().length >= 2
);

// Ações do header do Box
const headerActions = computed(() => [
    {
        type: 'button',
        icon: 'plus',
        text: 'Adicionar Arquivo',
        title: 'Fazer upload de um novo arquivo',
        key: 'upload-file'
    },
    {
        type: 'button',
        icon: 'cog',
        text: 'Gerenciar Categoria',
        title: 'Gerenciar categorias existentes',
        key: 'manage-categories'
    },
    {
        type: 'button',
        icon: 'office',
        text: 'Gerenciar Empresa',
        title: 'Gerenciar empresas existentes',
        key: 'manage-clients'
    }
]);

// ===========================
// FUNÇÕES PRINCIPAIS
// ===========================

// Carregamento dos arquivos
const loadFiles = async () => {
    try {
        loading.value = true;

        // Garantir que filtros estão limpos na inicialização
        Object.keys(filters.value).forEach(key => {
            filters.value[key] = '';
        });
        searchQuery.value = '';

        // Simular delay de API para demonstração
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Carregar dados dos arquivos (limitado a 20 para performance)
        allFiles.value = mockFiles.slice(0, 20);

        // Atualizar contadores de empresas
        updateClientCounts();

        console.log(`✅ ${allFiles.value.length} arquivos carregados com sucesso`);

    } catch (error) {
        console.error('❌ Erro ao carregar arquivos:', error);

        notify('Não foi possível carregar seus arquivos. Tente novamente.', 'error');
    } finally {
        loading.value = false;
    }
};

// Aplicação de filtros (reativo via computed)
const applyFilters = () => {
    // Filtros são aplicados automaticamente via computed properties
    console.log('🔍 Filtros aplicados:', {
        search: searchQuery.value,
        ...filters.value,
        results: filteredFiles.value.length
    });
};

// Métodos para o FilterSection
const updateSearch = (value) => {
    searchQuery.value = value;
    applyFilters();
};

const updateFilter = ({ key, value }) => {
    filters.value[key] = value;

    // Se mudou o tipo de pessoa, limpar filtros dependentes
    if (key === 'personType') {
        filters.value.type = '';
        filters.value.category = '';
        filters.value.client = '';
        console.log('🔄 Tipo de pessoa alterado, limpando filtros dependentes');
    }

    applyFilters();
};

// Reset completo dos filtros
const clearFilters = () => {
    // Limpar todos os filtros para estado inicial
    Object.keys(filters.value).forEach(key => {
        filters.value[key] = '';
    });
    searchQuery.value = '';

    console.log('🧹 Filtros limpos - mostrando todos os arquivos');
};

// Funções dos modais
const openCreateCategoryModal = () => {
    showCreateCategoryModal.value = true;
    newCategoryName.value = '';
};

const openManageClientsModal = () => {
    showManageClientsModal.value = true;
    editingClient.value = null;
    newClientName.value = '';
    newClientLogo.value = null;
};

const closeManageClientsModal = () => {
    showManageClientsModal.value = false;
    editingClient.value = null;
    newClientName.value = '';
    newClientLogo.value = null;
};

// Funções do modal de gerenciar categorias
const openManageCategoriesModal = () => {
    showManageCategoriesModal.value = true;
    editingCategory.value = null;
    newCategoryName.value = '';
};

const closeManageCategoriesModal = () => {
    showManageCategoriesModal.value = false;
    editingCategory.value = null;
    newCategoryName.value = '';
};

const startEditCategory = (category) => {
    editingCategory.value = category;
    newCategoryName.value = category.name;
};

const cancelEdit = () => {
    editingCategory.value = null;
    newCategoryName.value = '';
};

const saveCategory = async () => {
    if (!newCategoryName.value.trim()) return;

    try {
        if (editingCategory.value) {
            // Editar categoria existente
            const index = existingCategories.value.findIndex(c => c.id === editingCategory.value.id);
            if (index !== -1) {
                existingCategories.value[index].name = newCategoryName.value.trim();
            }
            notify(`Categoria "${newCategoryName.value}" foi atualizada com sucesso.`, 'success');
        } else {
            // Adicionar nova categoria
            const newId = Math.max(...existingCategories.value.map(c => c.id)) + 1;
            existingCategories.value.push({
                id: newId,
                name: newCategoryName.value.trim(),
                count: 0
            });
            notify(`Categoria "${newCategoryName.value}" foi criada com sucesso.`, 'success');
        }

        editingCategory.value = null;
        newCategoryName.value = '';
    } catch (error) {
        notify('Não foi possível salvar a categoria. Tente novamente.', 'error');
    }
};

// Abrir modal de confirmação de exclusão
const openConfirmDeleteModal = (category) => {
    categoryToDelete.value = category;
    showConfirmDeleteCategory.value = true;
};

// Fechar modal de confirmação de exclusão
const closeConfirmDeleteModal = () => {
    categoryToDelete.value = null;
    showConfirmDeleteCategory.value = false;
};

// Função para gerar descrição do alerta de exclusão
const getDeleteAlertDescription = () => {
    if (!categoryToDelete.value) return '';

    const fileCount = filesInCategoryToDelete.value.length;
    const categoryName = categoryToDelete.value.name;

    if (fileCount === 0) {
        return `A categoria '${categoryName}' não possui arquivos associados e pode ser excluída com segurança.`;
    }

    if (fileCount === 1) {
        return `Você está prestes a excluir a categoria '${categoryName}'. Existe 1 arquivo que perderá sua organização, mas permanecerá salvo no sistema.`;
    }

    return `Você está prestes a excluir a categoria '${categoryName}'. Existem ${fileCount} arquivos que perderão sua organização, mas permanecerão salvos no sistema.`;
};

// Confirmar exclusão da categoria
const confirmDeleteCategory = async () => {
    if (!categoryToDelete.value) return;

    try {
        const index = existingCategories.value.findIndex(c => c.id === categoryToDelete.value.id);
        if (index !== -1) {
            existingCategories.value.splice(index, 1);
            notify(`Categoria "${categoryToDelete.value.name}" foi excluída com sucesso.`, 'success');
        }
        closeConfirmDeleteModal();
    } catch (error) {
        notify('Não foi possível excluir a categoria. Tente novamente.', 'error');
    }
};

// Função para mudança de aba (categorias)
const onTabChange = (tabId) => {
    // Cancelar edição se estiver editando e mudar de aba
    if (editingCategory.value) {
        cancelEdit();
    }
    // Limpar formulário ao ir para aba de adicionar
    if (tabId === 'add') {
        newCategoryName.value = '';
        editingCategory.value = null;
    }
};

// ===========================
// FUNÇÕES DE CONFIRMAÇÃO DE EXCLUSÃO DE CLIENTE
// ===========================

// Abrir modal de confirmação de exclusão de cliente
const openConfirmDeleteClientModal = (client) => {
    clientToDelete.value = client;
    showConfirmDeleteClient.value = true;
};

// Fechar modal de confirmação de exclusão de cliente
const closeConfirmDeleteClientModal = () => {
    clientToDelete.value = null;
    showConfirmDeleteClient.value = false;
};

// Função para gerar descrição do alerta de exclusão de cliente
const getDeleteClientAlertDescription = () => {
    if (!clientToDelete.value) return '';

    const fileCount = filesInClientToDelete.value.length;
    const clientName = clientToDelete.value.name;

    if (fileCount === 0) {
        return `A empresa '${clientName}' não possui arquivos associados e pode ser excluída com segurança.`;
    }

    if (fileCount === 1) {
        return `Você está prestes a excluir a empresa '${clientName}'. Existe 1 arquivo que perderá sua associação com a empresa, mas permanecerá salvo no sistema.`;
    }

    return `Você está prestes a excluir a empresa '${clientName}'. Existem ${fileCount} arquivos que perderão sua associação com a empresa, mas permanecerão salvos no sistema.`;
};

// Confirmar exclusão do cliente
const confirmDeleteClient = async () => {
    if (!clientToDelete.value) return;

    try {
        const index = existingClients.value.findIndex(c => c.id === clientToDelete.value.id);
        if (index !== -1) {
            existingClients.value.splice(index, 1);
            notify(`Empresa "${clientToDelete.value.name}" foi excluída com sucesso.`, 'success');
        }
        closeConfirmDeleteClientModal();
    } catch (error) {
        notify('Não foi possível excluir a empresa. Tente novamente.', 'error');
    }
};

// ===========================
// FUNÇÕES DE GERENCIAR EMPRESAS
// ===========================

const startEditClient = (client) => {
    editingClient.value = client;
    newClientName.value = client.name;
};

const cancelEditClient = () => {
    editingClient.value = null;
    newClientName.value = '';
    newClientLogo.value = null;
};

const saveClient = async () => {
    if (!newClientName.value.trim()) return;

    try {
        if (editingClient.value) {
            // Editar empresa existente
            const index = existingClients.value.findIndex(c => c.id === editingClient.value.id);
            if (index !== -1) {
                existingClients.value[index].name = newClientName.value.trim();
            }
            notify(`Empresa "${newClientName.value}" foi atualizada com sucesso.`, 'success');
        } else {
            // Adicionar nova empresa
            const newId = Math.max(...existingClients.value.map(c => c.id)) + 1;
            existingClients.value.push({
                id: newId,
                name: newClientName.value.trim(),
                email: '',
                count: 0,
                image: newClientLogo.value || null
            });
            notify(`Empresa "${newClientName.value}" foi criada com sucesso.`, 'success');
        }

        editingClient.value = null;
        newClientName.value = '';
        newClientLogo.value = null;
    } catch (error) {
        notify('Não foi possível salvar a empresa. Tente novamente.', 'error');
    }
};

// Função para mudança de aba (empresas)
const onClientTabChange = (tabId) => {
    // Cancelar edição se estiver editando e mudar de aba
    if (editingClient.value) {
        cancelEditClient();
    }
    // Limpar formulário ao ir para aba de adicionar
    if (tabId === 'add') {
        newClientName.value = '';
        newClientLogo.value = null;
        editingClient.value = null;
    }
};

// Funções do FileCard
// Funções de view e download removidas - agora estão no FileCard


// ===========================
// HANDLERS DE AÇÕES
// ===========================

// Manipulação das ações do header
const handleHeaderAction = (action) => {
    const actionHandlers = {
        'manage-categories': openManageCategoriesModal,
        'manage-clients': openManageClientsModal,
        'upload-file': openUploadModal
    };

    const handler = actionHandlers[action.key];
    if (handler) {
        handler();
    } else {
        console.warn('⚠️ Ação não reconhecida:', action);
    }
};

// Função de abrir modal de upload removida daqui pois agora é injetada do Dashboard

// Lifecycle
onMounted(() => {
    console.log('🚀 MyFiles - Filtros iniciais:', filters.value);
    loadFiles();

    // Debug após carregamento
    setTimeout(() => {
        console.log('🔍 MyFiles - Filtros após carregamento:', filters.value);
        console.log('📊 MyFiles - Opções de tipo:', typeFilterOptions.value);
    }, 1500);
});
</script>

<style lang="scss" scoped>
// Importar mixins responsivos
@import '@/assets/css/mediaScreen.scss';

.myFiles {}

/* ===========================
   ESTATÍSTICAS
   =========================== */
.stats {
    display: flex;
    gap: 2em;
    align-items: center;
    margin-top: 1.5em;
    padding-top: 1.5em;
    border-top: 1px solid var(--borderColorLight, #e5e7eb);
}

.stats_item {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    position: relative;

    &:not(:last-child)::after {
        content: '';
        position: absolute;
        right: -1em;
        top: 50%;
        transform: translateY(-50%);
        width: 1px;
        height: 2em;
        background: var(--borderColorLight, #e5e7eb);
    }
}

.stats_number {
    font-size: 1.5em;
    font-weight: 700;
    color: var(--textPrimary, #1f2937);
    line-height: 1;
    margin-bottom: 0.25em;

    .stats_item--warning & {
        color: var(--colorWarning, #f59e0b);
    }

    .stats_item--danger & {
        color: var(--colorDanger, #ef4444);
    }
}

.stats_label {
    font-size: 0.75em;
    color: var(--textSecondary, #64748b);
    font-weight: 500;
    text-transform: lowercase;
}


/* ===========================
   FILTROS
   =========================== */
.filters_content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1.5em;
}

.filters_main {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.75em;
}

.filters_label {
    font-size: 0.75em;
    font-weight: 600;
    color: var(--textMuted, #6b7280);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.5em;
}

.filters_row {
    display: flex;
    gap: 0.75em;
    flex-wrap: wrap;
}

.filters_select {
    min-width: 160px;
    max-width: 200px;
    flex: 1;
}

.filters_search {
    min-width: 220px;
    max-width: 280px;
    flex: 1;
}

.filters_actions {
    display: flex;
    align-items: center;
    gap: 0.75em;
    flex-shrink: 0;
}



/* ===========================
   CONTEÚDO PRINCIPAL
   =========================== */
.myFiles_content {
    flex: 1;
}

.myFiles_loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4em;
}

.myFiles_empty {
    /* Classe disponível para manipulação futura */
}

.myFiles_grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 1em;
}

/* ===========================
   PAGINAÇÃO
   =========================== */
.myFiles_pagination {
    display: flex;
    justify-content: center;
    padding: 1.5em 0;
    border-top: 1px solid var(--borderColorLight, #e5e7eb);
}

.myFiles_paginationInfo {
    font-size: 0.875em;
    color: var(--textSecondary, #64748b);
    font-weight: 500;
}

/* ===========================
   MODAIS
   =========================== */

.createModal_content {
    padding: 1em 0;
}

.createModal_form {
    display: flex;
    flex-direction: column;
    gap: 1.5em;
}

.createModal_actions {
    display: flex;
    gap: 1em;
    justify-content: flex-end;
    padding-top: 1em;
    border-top: 1px solid var(--borderColorLight, #e5e7eb);
    margin-top: 0.5em;
}

/* ===========================
   RESPONSIVIDADE
   =========================== */
@include screen_medias(m) {
    .myFiles {
        padding: 0.75em;
        gap: 1em;
    }

    .stats {
        gap: 1.5em;
        justify-content: center;
        margin-top: 1em;
        padding-top: 1em;
    }

    .stats_item {
        &:not(:last-child)::after {
            height: 1.5em;
        }
    }

    .filters_content {
        flex-direction: column;
        gap: 1em;
        align-items: stretch;
    }

    .filters_label {
        font-size: 0.75em;
        margin-bottom: 0.5em;
    }

    .filters_row {
        flex-direction: column;
        gap: 0.75em;
    }

    .filters_select {
        min-width: auto;
        max-width: none;
    }

    .filters_search {
        max-width: none;
    }

    .filters_actions {
        align-self: stretch;
        justify-content: center;
    }


    .myFiles_grid {
        grid-template-columns: 1fr;
        gap: 1em;
    }

    .myFiles_empty {
        padding: 2.5em 1.5em;
        margin: 0.5em 0;
        gap: 1.25em;
        border-radius: 1em;
    }

    .myFiles_emptyIcon {
        width: 3.5em;
        height: 3.5em;
        margin-bottom: 0.25em;

        .sw-icon {
            font-size: 1.5em;
        }
    }

    .myFiles_emptyTitle {
        font-size: 1em;
    }

    .myFiles_emptySubtitle {
        font-size: 0.8125em;
        max-width: 300px;
    }

    .myFiles_emptyActions {
        gap: 0.5em;
        flex-direction: column;
        width: 100%;

        sw-button {
            width: 100%;
            max-width: 240px;
        }
    }

}

@include screen_medias(s) {
    .filters_row {
        gap: 0.75em;
    }
}

// Estilos para o modal de gerenciar categorias
.manage-categories {
    padding: 1.5em;

    .categories-list {
        min-height: 300px;

        .category-item {
            padding: 1.25em;
            border: 1px solid rgba(59, 130, 246, 0.1);
            border-radius: 0.75em;
            margin-bottom: 1em;
            background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
            height: 100%;
            display: flex;
            flex-direction: column;

            &:hover {
                background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                border-color: rgba(59, 130, 246, 0.2);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
            }

            .category-header {
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 1em;

                .category-icon {
                    width: 2.5em;
                    height: 2.5em;
                    border-radius: 0.5em;
                    background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.125em;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
                    flex-shrink: 0;
                }
            }

            .category-content {
                flex: 1;

                .category-info {
                    display: flex;
                    flex-direction: column;
                    text-align: center;

                    .category-name {
                        font-weight: 600;
                        color: var(--textPrimary, #1f2937);
                        font-size: 0.95em;
                        line-height: 1.4;
                        margin-bottom: 0.5em;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .category-count {
                        font-size: 0.8em;
                        color: var(--textSecondary, #64748b);
                        font-weight: 500;
                        opacity: 0.8;
                        margin-bottom: 1em;
                    }

                    .category-actions {
                        display: flex;
                        gap: 0.375em;
                        justify-content: center;
                        margin-top: auto;
                        padding-top: 0.75em;
                        border-top: 1px solid rgba(229, 231, 235, 0.5);
                    }
                }

                .category-edit {
                    .edit-actions {
                        display: flex;
                        gap: 0.5em;
                        margin-top: 1em;
                        justify-content: flex-end;
                    }
                }
            }
        }

        // Responsividade mobile
        @include screen_medias(s) {
            .category-item {
                padding: 1em;
                border-radius: 0.5em;

                .category-header {
                    margin-bottom: 0.75em;

                    .category-icon {
                        width: 2em;
                        height: 2em;
                        font-size: 1em;
                    }

                    .category-actions {
                        opacity: 1; // Sempre visível no mobile
                        gap: 0.25em;
                    }
                }

                .category-content {
                    .category-info {
                        .category-name {
                            font-size: 0.9em;
                            margin-bottom: 0.375em;
                        }

                        .category-count {
                            font-size: 0.75em;
                        }
                    }

                    .category-edit {
                        .edit-actions {
                            flex-direction: column;
                            gap: 0.5em;
                            margin-top: 0.75em;

                            sw-button {
                                width: 100%;
                            }
                        }
                    }
                }
            }
        }

        .empty-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 4em 2em;
            text-align: center;
            color: var(--textSecondary);
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            border: 1px solid rgba(59, 130, 246, 0.1);
            border-radius: 1em;
            margin: 1em 0;

            sw-icon {
                font-size: 3.5em;
                margin-bottom: 1.25em;
                color: rgba(59, 130, 246, 0.3);
                opacity: 0.7;
                padding: 1em;
                background: rgba(59, 130, 246, 0.05);
                border-radius: 50%;
                border: 2px solid rgba(59, 130, 246, 0.1);
            }

            p {
                font-size: 1.1em;
                font-weight: 600;
                margin-bottom: 0.75em;
                color: var(--textPrimary, #1f2937);
            }

            small {
                font-size: 0.9em;
                opacity: 0.8;
                line-height: 1.5;
                max-width: 300px;
            }
        }
    }

    .add-category {
        .add-category-form {
            display: flex;
            flex-direction: column;
            gap: 1.5em;

            .form-actions {
                display: flex;
                justify-content: flex-end;
            }
        }
    }
}

// Estilos para o modal de gerenciar empresas
.manage-clients {
    padding: 1.5em;

    .clients-list {
        min-height: 300px;

        .client-item {
            padding: 1.25em;
            border: 1px solid rgba(59, 130, 246, 0.1);
            border-radius: 0.75em;
            margin-bottom: 1em;
            background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
            height: 100%;
            display: flex;
            flex-direction: column;

            &:hover {
                background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                border-color: rgba(59, 130, 246, 0.2);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
            }

            .client-header {
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 1em;

                .client-icon {
                    width: 2.5em;
                    height: 2.5em;
                    border-radius: 0.5em;
                    background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.125em;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
                    flex-shrink: 0;
                    overflow: hidden;
                    position: relative;

                    .client-logo {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                        border-radius: 0.5em;
                    }
                }
            }

            .client-content {
                flex: 1;

                .client-info {
                    display: flex;
                    flex-direction: column;
                    text-align: center;

                    .client-name {
                        font-weight: 600;
                        color: var(--textPrimary, #1f2937);
                        font-size: 0.95em;
                        line-height: 1.4;
                        margin-bottom: 0.5em;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .client-count {
                        font-size: 0.8em;
                        color: var(--textSecondary, #64748b);
                        font-weight: 500;
                        opacity: 0.8;
                        margin-bottom: 1em;
                    }

                    .client-actions {
                        display: flex;
                        gap: 0.375em;
                        justify-content: center;
                        margin-top: auto;
                        padding-top: 0.75em;
                        border-top: 1px solid rgba(229, 231, 235, 0.5);
                    }
                }

                .client-edit {
                    .edit-actions {
                        display: flex;
                        gap: 0.5em;
                        margin-top: 1em;
                        justify-content: flex-end;
                    }
                }
            }
        }

        // Responsividade mobile
        @include screen_medias(s) {
            .client-item {
                padding: 1em;
                border-radius: 0.5em;

                .client-header {
                    margin-bottom: 0.75em;

                    .client-icon {
                        width: 2em;
                        height: 2em;
                        font-size: 1em;
                    }
                }

                .client-content {
                    .client-info {
                        .client-name {
                            font-size: 0.9em;
                            margin-bottom: 0.375em;
                        }

                        .client-count {
                            font-size: 0.75em;
                        }

                        .client-actions {
                            padding-top: 0.5em;
                            gap: 0.25em;
                        }
                    }

                    .client-edit {
                        .edit-actions {
                            flex-direction: column;
                            gap: 0.5em;
                            margin-top: 0.75em;

                            sw-button {
                                width: 100%;
                            }
                        }
                    }
                }
            }
        }
    }

    .add-client {
        .add-client-form {
            display: flex;
            flex-direction: column;
            gap: 1.5em;

            .form-actions {
                display: flex;
                justify-content: flex-end;
            }
        }
    }
}

/* ===========================
   MODAL CONFIRMAR EXCLUSÃO
   =========================== */
.confirmDeleteModal_content {
    padding: 1.5em 0;
}

.confirmDelete_info {
    display: flex;
    flex-direction: column;
    gap: 1.5em;
}

.confirmDelete_categoryDetails {
    display: flex;
    flex-direction: column;
    gap: 1.5em;
}

.categoryCard {
    padding: 1.25em;
    background: linear-gradient(135deg, rgba(248, 250, 252, 0.9), rgba(241, 245, 249, 0.8));
    border: 1px solid rgba(229, 231, 235, 0.6);
    border-radius: 0.75em;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.categoryCard_header {
    display: flex;
    align-items: center;
    gap: 1em;
}

.categoryCard_icon {
    width: 3em;
    height: 3em;
    border-radius: 0.5em;
    background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25em;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
    flex-shrink: 0;
    overflow: hidden;
    position: relative;
}

.categoryCard_logo {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0.5em;
}

.categoryCard_info {
    flex: 1;

    h4 {
        margin: 0 0 0.375em 0;
        color: var(--textPrimary, #1f2937);
        font-size: 1.125em;
        font-weight: 600;
    }

    .file-count {
        color: var(--textSecondary, #64748b);
        font-size: 0.875em;
        font-weight: 500;
    }

    .client-email {
        color: var(--textSecondary, #64748b);
        font-size: 0.8em;
        opacity: 0.8;
        margin-top: 0.25em;
        display: block;
    }
}

.confirmDelete_filesList {
    h5 {
        margin: 0 0 1em 0;
        color: var(--textPrimary, #1f2937);
        font-size: 0.9em;
        font-weight: 600;
    }
}

.filesList {
    display: flex;
    flex-direction: column;
    gap: 0.5em;
    max-height: 200px;
    overflow-y: auto;
    padding: 0.75em;
    background: white;
    border: 1px solid rgba(229, 231, 235, 0.6);
    border-radius: 0.5em;
    margin-bottom: 1em;
}

.fileItem {
    display: flex;
    align-items: center;
    gap: 0.75em;
    padding: 0.75em;
    background: rgba(248, 250, 252, 0.8);
    border: 1px solid rgba(229, 231, 235, 0.3);
    border-radius: 0.5em;
    font-size: 0.875em;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(241, 245, 249, 0.9);
        border-color: rgba(59, 130, 246, 0.2);
    }

    .sw-icon {
        color: var(--colorBlue, #3b82f6);
        font-size: 1.125em;
        flex-shrink: 0;
    }

    span:first-of-type {
        color: var(--textPrimary, #1f2937);
        font-weight: 500;
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .file-type {
        color: var(--textSecondary, #64748b);
        font-size: 0.8em;
        background: rgba(148, 163, 184, 0.1);
        padding: 0.25em 0.5em;
        border-radius: 0.25em;
        flex-shrink: 0;
    }
}

.moreFiles {
    padding: 0.75em;
    text-align: center;
    color: var(--textSecondary, #64748b);
    font-size: 0.8em;
    font-weight: 500;
    border-top: 1px solid rgba(229, 231, 235, 0.5);
    background: rgba(248, 250, 252, 0.4);
    margin-top: 0.5em;
    border-radius: 0 0 0.5em 0.5em;
}

.impact-info {
    margin-top: 1.25em;
    padding: 1em;
    background: rgba(239, 246, 255, 0.8);
    border: 1px solid rgba(59, 130, 246, 0.15);
    border-radius: 0.75em;
    display: flex;
    flex-direction: column;
    gap: 0.75em;
}

.impact-item {
    display: flex;
    align-items: center;
    gap: 0.75em;
    font-size: 0.875em;

    .sw-icon {
        color: var(--colorGreen, #10b981);
        font-size: 1.125em;
        flex-shrink: 0;
        background: rgba(16, 185, 129, 0.1);
        border-radius: 50%;
        padding: 0.25em;
        width: 1.75em;
        height: 1.75em;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    span {
        color: var(--textPrimary, #1f2937);
        line-height: 1.4;

        strong {
            color: var(--colorBlue, #3b82f6);
        }
    }
}

.confirmDeleteActions {
    display: flex;
    gap: 1em;
    justify-content: flex-end;
    padding-top: 1.5em;
    border-top: 1px solid var(--borderColorLight, #e5e7eb);
    margin-top: 1em;
}

/* Responsividade para modal de confirmação */
@include screen_medias(s) {
    .confirmDeleteModal_content {
        padding: 1em 0;
    }

    .categoryCard {
        padding: 1em;
    }

    .categoryCard_icon {
        width: 2.5em;
        height: 2.5em;
        font-size: 1.125em;

        .categoryCard_logo {
            border-radius: 0.375em;
        }
    }

    .categoryCard_info {
        h4 {
            font-size: 1em;
        }

        .file-count {
            font-size: 0.8em;
        }
    }

    .confirmDeleteActions {
        flex-direction: column;
        gap: 0.75em;

        sw-button {
            width: 100%;
        }
    }

    .filesList {
        max-height: 150px;
        padding: 0.5em;
    }

    .fileItem {
        padding: 0.5em;
        font-size: 0.8em;
        gap: 0.5em;

        .file-type {
            font-size: 0.75em;
            padding: 0.2em 0.4em;
        }
    }

    .impact-info {
        padding: 0.75em;
        gap: 0.5em;
    }

    .impact-item {
        font-size: 0.8em;
        gap: 0.5em;

        .sw-icon {
            width: 1.5em;
            height: 1.5em;
            font-size: 1em;
        }
    }
}
</style>